/* ---------- shared ---------- */
.cover {
  position: relative;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  overflow: hidden;
  background: linear-gradient(135deg, var(--surface-3), var(--surface-1));
  color: var(--text-3);
}

.cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rows {
  display: flex;
  flex-direction: column;
  padding: 0 8px;
}

.rowSkeleton {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
}

.rowText {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}

.rowTitle,
.rowSub {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rowTitle {
  font-weight: 700;
  font-size: var(--fs-md);
}

.rowSub {
  font-size: var(--fs-sm);
  color: var(--text-3);
  display: flex;
  align-items: center;
  gap: 6px;
}

.previewTag {
  font-size: 10px;
  font-weight: 800;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgb(255 181 71 / 0.15);
  color: var(--warning);
  letter-spacing: 0.02em;
}

.textBtn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: var(--fs-sm);
  font-weight: 700;
  color: var(--accent);
}

/* ---------- track row ---------- */
.trackRow {
  display: flex;
  align-items: center;
  gap: 2px;
  border-radius: var(--r-md);
  transition: background var(--t-fast);
}

.trackRow:hover {
  background: var(--surface-1);
}

.trackMain {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  text-align: left;
}

.trackCurrent .rowTitle {
  color: var(--accent);
}

.trackUnavailable {
  opacity: 0.45;
}

.trackNumber {
  width: 22px;
  text-align: center;
  color: var(--text-3);
  font-weight: 700;
  font-size: var(--fs-sm);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.trackCoverWrap {
  position: relative;
  flex-shrink: 0;
}

.coverOverlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgb(0 0 0 / 0.5);
  border-radius: 6px;
  color: var(--accent);
}

.eq {
  display: inline-flex;
  align-items: flex-end;
  gap: 2px;
  height: 14px;
  color: var(--accent);
}

.eq i {
  width: 3px;
  background: currentColor;
  border-radius: 1px;
  animation: eq 0.9s ease-in-out infinite;
}

.eq i:nth-child(2) {
  animation-delay: -0.3s;
}

.eq i:nth-child(3) {
  animation-delay: -0.6s;
}

@keyframes eq {
  0%,
  100% {
    height: 30%;
  }
  50% {
    height: 100%;
  }
}

/* ---------- music tab ---------- */
.musicHeader {
  padding: calc(var(--safe-t) + 18px) var(--gutter) 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.musicHeader h1 {
  font-size: var(--fs-2xl);
  font-weight: 800;
  letter-spacing: -0.03em;
}

.searchBar {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 16px;
  border-radius: var(--r-md);
  background: var(--surface-2);
  color: var(--text-3);
  font-weight: 600;
  font-size: var(--fs-md);
  text-align: left;
}

.likedCard,
.newCard {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 10px;
  width: 148px;
  height: 148px;
  padding: 14px;
  border-radius: 14px;
  font-weight: 800;
  font-size: var(--fs-md);
  text-align: left;
}

.likedCard {
  background: linear-gradient(140deg, var(--accent) 0%, #067a46 55%, #032417 100%);
  color: var(--accent-ink);
}

.newCard {
  background: var(--surface-1);
  border: 1.5px dashed var(--border-strong);
  color: var(--text-2);
  align-items: flex-start;
}

.newCard:hover {
  border-color: var(--accent);
  color: var(--accent);
}

/* ---------- cards ---------- */
.card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 148px;
  gap: 2px;
}

.cardArt {
  display: block;
  width: 148px;
  height: 148px;
  margin-bottom: 8px;
  transition: transform var(--t-fast) var(--ease);
}

.card:hover .cardArt {
  transform: translateY(-2px);
}

.cardPlay {
  position: absolute;
  top: 104px;
  right: 8px;
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent);
  color: var(--accent-ink);
  box-shadow: 0 6px 18px rgb(0 0 0 / 0.5);
  transition: transform var(--t-fast) var(--ease-spring);
}

.cardPlay .eq {
  color: var(--accent-ink);
}

.cardPlay:active {
  transform: scale(0.9);
}

.cardTitle,
.cardSub {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cardTitle {
  font-size: var(--fs-sm);
  font-weight: 700;
}

.cardSub {
  font-size: var(--fs-xs);
  color: var(--text-3);
}

/* ---------- sheets ---------- */
.sheetPad {
  padding: 0 12px 20px;
}

.newPlaylist {
  display: grid;
  gap: 12px;
  padding: 8px;
}

.listRow {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px;
  border-radius: var(--r-md);
  text-align: left;
}

.listRow:hover {
  background: var(--surface-2);
}

.newIcon {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: var(--surface-3);
  color: var(--accent);
}

/* ---------- sound / artist / album heroes ---------- */
.hero {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 16px;
  padding: 8px var(--gutter) 0;
}

.heroText {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  justify-content: center;
}

.heroText h1 {
  font-size: var(--fs-xl);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.heroArtist {
  font-weight: 700;
  color: var(--text-2);
}

.heroArtist:hover {
  color: var(--text);
}

.heroMeta {
  font-size: var(--fs-xs);
  color: var(--text-3);
}

.rightsNote {
  font-size: var(--fs-xs);
  color: var(--warning);
}

.heroActions {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  gap: 6px;
}

.hero > button:last-child {
  grid-column: 1 / -1;
}

.gridTitle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 var(--gutter);
  margin: 28px 0 12px;
  font-size: var(--fs-md);
  font-weight: 800;
}

.artistHero,
.collectionHero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 8px var(--gutter) 16px;
}

.artistHero h1,
.collectionHero h1 {
  font-size: var(--fs-2xl);
  font-weight: 800;
  letter-spacing: -0.03em;
}

.artistHero p,
.collectionHero p {
  color: var(--text-2);
  font-size: var(--fs-sm);
  max-width: 340px;
  line-height: 1.5;
}

.collectionHero p a {
  font-weight: 700;
  color: var(--text);
}

.collectionActions {
  display: flex;
  gap: 10px;
  margin-top: 6px;
}

.likedArt {
  display: grid;
  place-items: center;
  width: 200px;
  height: 200px;
  border-radius: 16px;
  background: linear-gradient(140deg, var(--accent) 0%, #067a46 55%, #032417 100%);
  color: var(--accent-ink);
}
